import React from 'react';
import { motion } from 'framer-motion';
import {
  Building2, Quote, Star, Globe, Briefcase,
} from 'lucide-react';
import PageHero from '../components/shared/PageHero';
import StatsBar from '../components/shared/StatsBar';
import CTABanner from '../components/shared/CTABanner';
import TechPattern from '../components/shared/TechPattern';

const clients = [
  {
    name: 'Globo.com',
    industry: 'Media & Technology',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/3f/Globo.com_logo.svg/200px-Globo.com_logo.svg.png',
    agents: 25,
    result: 'Accelerated content delivery pipelines with AI-powered engineering and automated quality validation.',
    color: '#2563eb',
  },
  {
    name: 'LuizaLabs',
    industry: 'E-commerce & Technology',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a2/Magalu_-_novo_logo.png/200px-Magalu_-_novo_logo.png',
    agents: 20,
    result: 'Streamlined engineering operations with AI agents handling code reviews and automated testing at scale.',
    color: '#7c3aed',
  },
];

const testimonials = [
  {
    quote: 'We went from a 2-week code review backlog to same-day reviews. The agents catch issues our team was missing.',
    name: 'Sarah Chen',
    role: 'VP of Engineering',
    company: 'Globo.com',
    color: '#2563eb',
  },
  {
    quote: 'Having AI agents working alongside our engineers transformed how we handle code reviews and deployments. The consistency is remarkable.',
    name: 'Marcus Rivera',
    role: 'CTO',
    company: 'LuizaLabs',
    color: '#7c3aed',
  },
];

const industries = [
  { name: 'Media & Entertainment', icon: Globe, color: '#2563eb' },
  { name: 'E-commerce & Retail', icon: Briefcase, color: '#7c3aed' },
];

const ClientsPage: React.FC = () => {
  return (
    <>
      <PageHero
        label="Our Clients"
        labelIcon={<Building2 size={14} />}
        title={<>Companies That <span className="gradient-text">Trust</span> AI Team</>}
        subtitle="Leading companies are transforming their engineering operations with our AI-powered teams."
      />

      <section style={{ paddingBottom: 80 }}>
        <div className="container">
          <StatsBar />
        </div>
      </section>

      {/* Client Cards */}
      <section className="section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: 64 }}
          >
            <h2 className="section-title" style={{ margin: '0 auto 16px' }}>
              Real Results from <span className="gradient-text-cyan">Real Companies</span>
            </h2>
          </motion.div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 24,
            maxWidth: 800,
            margin: '0 auto',
          }}>
            {clients.map((client, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -6 }}
                style={{
                  background: 'white',
                  borderRadius: 'var(--radius-lg)',
                  padding: '32px 28px',
                  border: '1px solid var(--border-color)',
                  boxShadow: 'var(--shadow-card)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                  background: client.color,
                }} />
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20,
                }}>
                  <img
                    src={client.logo}
                    alt={client.name}
                    style={{ height: 40, width: 'auto', objectFit: 'contain' }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <div>
                    <h4 style={{ fontSize: '1.15rem', fontWeight: 700 }}>{client.name}</h4>
                    <span style={{
                      fontSize: '0.72rem', padding: '3px 10px',
                      background: `${client.color}08`, borderRadius: 100,
                      color: client.color, fontWeight: 600,
                    }}>
                      {client.industry}
                    </span>
                  </div>
                </div>
                <div style={{
                  display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 12,
                }}>
                  <span style={{
                    fontSize: '1.8rem', fontWeight: 800, fontFamily: 'Space Grotesk', color: client.color,
                  }}>
                    {client.agents}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>active agents</span>
                </div>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  {client.result}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section" style={{ position: 'relative', overflow: 'hidden' }}>
        <TechPattern variant="nodes" opacity={0.025} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: 64 }}
          >
            <span className="section-label"><Star size={14} />Testimonials</span>
            <h2 className="section-title" style={{ margin: '0 auto 16px' }}>
              What Our Clients <span className="gradient-text">Say</span>
            </h2>
          </motion.div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 24,
            maxWidth: 800,
            margin: '0 auto',
          }}>
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                style={{
                  background: 'white',
                  borderRadius: 'var(--radius-lg)',
                  padding: '32px 24px',
                  border: '1px solid var(--border-color)',
                  boxShadow: 'var(--shadow-card)',
                }}
              >
                <Quote size={24} color={t.color} style={{ opacity: 0.2, marginBottom: 16 }} />
                <p style={{
                  fontSize: '0.95rem', color: 'var(--text-secondary)', lineHeight: 1.7,
                  marginBottom: 24, fontStyle: 'italic',
                }}>
                  "{t.quote}"
                </p>
                <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{t.name}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{t.role}, {t.company}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: 48 }}
          >
            <h2 className="section-title" style={{ margin: '0 auto 16px' }}>
              Industries We <span className="gradient-text-cyan">Serve</span>
            </h2>
          </motion.div>

          <div style={{
            display: 'flex', justifyContent: 'center', gap: 24,
          }}>
            {industries.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}
                  whileHover={{ y: -4 }}
                  style={{
                    background: 'white', borderRadius: 'var(--radius)', padding: '28px 32px',
                    border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)', textAlign: 'center',
                  }}
                >
                  <Icon size={28} color={ind.color} style={{ marginBottom: 12 }} />
                  <div style={{ fontSize: '0.9rem', fontWeight: 700 }}>{ind.name}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <CTABanner
        title={<>Join Leading Companies Using <span className="gradient-text">AI Team</span></>}
        subtitle="Start with a free diagnostic and see where AI can generate the most impact for your organization."
      />
    </>
  );
};

export default ClientsPage;
